/**
 * SitePhotoRecovery — iter386f admin page.
 *
 * DRY RUN + APPLY flow that matches the migrations-runner pattern:
 *   • Project ID is pinned to Turnbull (7c4b62f7-…) by default and
 *     editable in a text input for future re-use on other projects
 *     (still scoped one-at-a-time; never batch).
 *   • DRY RUN posts to /api/admin/site-photo-recovery/dry-run.
 *   • APPLY posts to /api/admin/site-photo-recovery/apply, but only
 *     after (a) a dry run has completed AND (b) the operator has
 *     typed "APPLY" into a confirmation input.
 *   • The apply endpoint itself also enforces the confirmation word.
 */
import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Check, Copy, Play, ShieldAlert } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const TURNBULL_PROJECT_ID = '7c4b62f7-41f6-4305-be85-0c0bcbbbd622';

export default function SitePhotoRecovery() {
  const [projectId, setProjectId] = useState(TURNBULL_PROJECT_ID);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmWord, setConfirmWord] = useState('');
  const [busy, setBusy] = useState(false);

  const runDry = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    setApplyResult(null);
    setDryRunResult(null);
    try {
      const started = await axios.post(`${API}/admin/site-photo-recovery/dry-run`, { project_id: projectId });
      const jobId = started.data.job_id;
      const total = started.data.photo_total || 0;
      // Poll every 2s until completed/failed
      let finalJob = null;
      for (let i = 0; i < 300; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(res => setTimeout(res, 2000));
        // eslint-disable-next-line no-await-in-loop
        const jr = await axios.get(`${API}/admin/site-photo-recovery/jobs/${jobId}`);
        const j = jr.data;
        setDryRunResult({ __progress: true, job: j, photo_total: total });
        if (j.status === 'completed' || j.status === 'failed') { finalJob = j; break; }
      }
      if (finalJob && finalJob.status === 'completed') {
        setDryRunResult(finalJob.result);
        toast.success(`Dry run: ${finalJob.result.found_count}/${finalJob.result.photo_count} located`);
      } else if (finalJob) {
        toast.error(`Dry run failed: ${finalJob.error || 'unknown'}`);
      } else {
        toast.error('Dry run timed out client-side');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dry run failed');
    } finally {
      setBusy(false);
    }
  }, [projectId]);

  const runApply = useCallback(async () => {
    if (!projectId) return;
    if (confirmWord !== 'APPLY') { toast.error('Type APPLY exactly to confirm'); return; }
    setBusy(true);
    try {
      const started = await axios.post(`${API}/admin/site-photo-recovery/apply`, {
        project_id: projectId, confirmation_word: 'APPLY',
      });
      const jobId = started.data.job_id;
      let finalJob = null;
      for (let i = 0; i < 300; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(res => setTimeout(res, 2000));
        // eslint-disable-next-line no-await-in-loop
        const jr = await axios.get(`${API}/admin/site-photo-recovery/jobs/${jobId}`);
        const j = jr.data;
        setApplyResult({ __progress: true, job: j });
        if (j.status === 'completed' || j.status === 'failed') { finalJob = j; break; }
      }
      if (finalJob && finalJob.status === 'completed') {
        setApplyResult(finalJob.result);
        toast.success(`Apply: ${finalJob.result.rewritten_count} rewritten, ${finalJob.result.skipped_count} skipped`);
        setConfirmWord('');
      } else if (finalJob) {
        toast.error(`Apply failed: ${finalJob.error || 'unknown'}`);
      } else {
        toast.error('Apply timed out client-side');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    } finally {
      setBusy(false);
    }
  }, [projectId, confirmWord]);

  const applyEnabled = useMemo(
    () => !!dryRunResult && !dryRunResult.__progress && dryRunResult.found_count > 0 && confirmWord === 'APPLY' && !busy,
    [dryRunResult, confirmWord, busy]
  );

  const copyTable = async () => {
    if (!dryRunResult) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(dryRunResult, null, 2));
      toast.success('Dry-run table copied');
    } catch (_e) { toast.error('Copy failed'); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4">
      <Card data-testid="site-photo-recovery-card">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            Site Photo Recovery
            <Badge variant="secondary" className="text-xs">iter386f</Badge>
          </CardTitle>
          <CardDescription>
            Rewrites <code>site_visits[*].photos[*].url</code> for a single
            project from a dead Dropbox share URL to an app-served
            <code>/api/uploads/photos/…</code> path.  The file is then fetched
            through the authenticated Dropbox refresh token — no public
            shared link is created or required.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
            <div>
              <label className="text-xs text-muted-foreground">Project ID</label>
              <Input
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                data-testid="recovery-project-id"
                placeholder="project uuid"
              />
            </div>
            <Button onClick={runDry} disabled={busy || !projectId} data-testid="recovery-dry-run-btn">
              <Play className="w-4 h-4 mr-1" /> Dry run
            </Button>
          </div>

          {dryRunResult && dryRunResult.__progress && (
            <div className="border rounded-md p-3 bg-blue-50 text-sm" data-testid="recovery-dry-run-progress">
              <b>Dry run in progress</b> — status: {dryRunResult.job?.status} · processed: {dryRunResult.job?.progress?.processed}/{dryRunResult.photo_total} · found: {dryRunResult.job?.progress?.found} · downloadable: {dryRunResult.job?.progress?.downloadable}
            </div>
          )}

          {dryRunResult && !dryRunResult.__progress && (
            <div className="border rounded-md" data-testid="recovery-dry-run-result">
              <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
                <div className="text-sm">
                  <b>{dryRunResult.project_name}</b>
                  <span className="text-muted-foreground"> · visits {dryRunResult.visit_count} · photos {dryRunResult.photo_count} · found {dryRunResult.found_count} · downloadable {dryRunResult.downloadable_count} · {dryRunResult.elapsed_ms} ms</span>
                </div>
                <Button variant="outline" size="sm" onClick={copyTable} data-testid="recovery-copy-table-btn">
                  <Copy className="w-3 h-3 mr-1" /> Copy JSON
                </Button>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background border-b">
                    <tr className="text-left">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">filename</th>
                      <th className="px-3 py-2">found</th>
                      <th className="px-3 py-2">namespace</th>
                      <th className="px-3 py-2">downloadable</th>
                      <th className="px-3 py-2">path</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dryRunResult.rows.map((r, i) => (
                      <tr key={`${r.visit_id}-${r.photo_index}`} className="border-b hover:bg-muted/20">
                        <td className="px-3 py-1 font-mono text-[10px]">{i + 1}</td>
                        <td className="px-3 py-1 truncate max-w-[240px]">{r.filename || '—'}</td>
                        <td className="px-3 py-1">{r.found ? <Check className="w-3 h-3 text-green-600" /> : <span className="text-amber-600">—</span>}</td>
                        <td className="px-3 py-1"><Badge variant="outline" className="text-[10px]">{r.namespace || '—'}</Badge></td>
                        <td className="px-3 py-1">{r.downloadable ? <Check className="w-3 h-3 text-green-600" /> : (r.download_error ? <span title={r.download_error} className="text-red-600 text-[10px]">err</span> : '—')}</td>
                        <td className="px-3 py-1 truncate max-w-[360px] font-mono text-[10px]">{r.path || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-2 text-amber-700">
              <ShieldAlert className="w-4 h-4" /> Apply is a write.  Type <b>APPLY</b> to enable.
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
              <Input
                value={confirmWord}
                onChange={(e) => setConfirmWord(e.target.value)}
                placeholder='Type APPLY to confirm'
                data-testid="recovery-confirm-input"
              />
              <Button
                onClick={runApply}
                disabled={!applyEnabled}
                data-testid="recovery-apply-btn"
                variant="destructive"
              >
                <AlertTriangle className="w-4 h-4 mr-1" /> Apply
              </Button>
            </div>
          </div>

          {applyResult && applyResult.__progress && (
            <div className="border rounded-md p-3 bg-blue-50 text-sm" data-testid="recovery-apply-progress">
              <b>Apply in progress</b> — status: {applyResult.job?.status} · processed: {applyResult.job?.progress?.processed} · rewritten: {applyResult.job?.progress?.rewritten} · skipped: {applyResult.job?.progress?.skipped}
            </div>
          )}

          {applyResult && !applyResult.__progress && (
            <div className="border rounded-md" data-testid="recovery-apply-result">
              <div className="px-3 py-2 bg-muted/40 border-b text-sm">
                <b>{applyResult.project_name}</b> · rewritten <b>{applyResult.rewritten_count}</b> · skipped <b>{applyResult.skipped_count}</b>
              </div>
              <pre className="p-3 text-[11px] font-mono max-h-[300px] overflow-auto">
                {JSON.stringify(applyResult, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
